import React, { useEffect, useRef, useState } from "react";
import api, { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Package, ShoppingBag, Users, IndianRupee, Plus, Edit2, Trash2, Upload, Tag } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = ["grocery", "restaurant", "fruits", "vegetables"];
const ORDER_STATUSES = ["placed", "confirmed", "out_for_delivery", "delivered", "cancelled"];

const emptyProduct = { name: "", category: "grocery", description: "", price: "", unit: "", image_url: "", stock: 100, featured: false };
const emptyCoupon = { code: "", type: "percent", value: 10, min_subtotal: 0, max_discount: "", expires_at: "", active: true, description: "" };

const Admin = () => {
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyProduct);
  const [open, setOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const [couponForm, setCouponForm] = useState(emptyCoupon);
  const [couponOpen, setCouponOpen] = useState(false);

  const loadAll = async () => {
    try {
      const [s, p, o, c] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/products", { params: { limit: 500 } }),
        api.get("/orders"),
        api.get("/coupons"),
      ]);
      setStats(s.data); setProducts(p.data); setOrders(o.data); setCoupons(c.data);
    } catch { toast.error("Failed to load admin data"); }
  };

  useEffect(() => { loadAll(); }, []);

  const openNew = () => { setEditing(null); setForm(emptyProduct); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ ...p }); setOpen(true); };

  const uploadImage = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      // Backend returns url like "/api/files/gramo/products/xxx.jpg" — prepend backend origin
      const backendOrigin = API.replace(/\/api$/, "");
      const finalUrl = data.url.startsWith("http") ? data.url : `${backendOrigin}${data.url}`;
      setForm((f) => ({ ...f, image_url: finalUrl }));
      toast.success("Image uploaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const saveProduct = async () => {
    const payload = { ...form, price: Number(form.price), stock: Number(form.stock || 0) };
    try {
      if (editing) { await api.put(`/products/${editing.product_id}`, payload); toast.success("Product updated"); }
      else { await api.post("/products", payload); toast.success("Product added"); }
      setOpen(false); loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const deleteProduct = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    try { await api.delete(`/products/${id}`); toast.success("Deleted"); loadAll(); }
    catch { toast.error("Delete failed"); }
  };

  const setOrderStatus = async (id, status) => {
    try { await api.put(`/orders/${id}/status`, { order_status: status }); toast.success("Order updated"); loadAll(); }
    catch { toast.error("Update failed"); }
  };

  const saveCoupon = async () => {
    const payload = {
      ...couponForm,
      value: Number(couponForm.value),
      min_subtotal: Number(couponForm.min_subtotal || 0),
      max_discount: couponForm.max_discount ? Number(couponForm.max_discount) : null,
      expires_at: couponForm.expires_at || null,
    };
    try {
      await api.post("/coupons", payload);
      toast.success("Coupon created");
      setCouponForm(emptyCoupon); setCouponOpen(false); loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const deleteCoupon = async (code) => {
    if (!window.confirm(`Delete coupon ${code}?`)) return;
    await api.delete(`/coupons/${code}`); toast.success("Deleted"); loadAll();
  };

  return (
    <div className="mx-auto max-w-7xl px-6 md:px-10 py-12" data-testid="admin-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="tick-label mb-2">Admin</div>
          <h1 className="font-heading text-4xl text-[#2C3E2D] font-medium">Control Room</h1>
        </div>
        <div className="flex flex-wrap gap-2">
          <TabBtn value="overview" tab={tab} setTab={setTab}>Overview</TabBtn>
          <TabBtn value="products" tab={tab} setTab={setTab}>Products</TabBtn>
          <TabBtn value="orders" tab={tab} setTab={setTab}>Orders</TabBtn>
          <TabBtn value="coupons" tab={tab} setTab={setTab}>Coupons</TabBtn>
        </div>
      </div>

      {tab === "overview" && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-5" data-testid="admin-overview">
          <Stat Icon={ShoppingBag} label="Total Orders" value={stats?.total_orders ?? "—"} testid="stat-orders" />
          <Stat Icon={Package} label="Products" value={stats?.total_products ?? "—"} testid="stat-products" />
          <Stat Icon={Users} label="Users" value={stats?.total_users ?? "—"} testid="stat-users" />
          <Stat Icon={IndianRupee} label="Revenue" value={stats ? `₹${stats.revenue.toFixed(0)}` : "—"} testid="stat-revenue" />
        </div>
      )}

      {tab === "products" && (
        <div>
          <div className="flex justify-between items-center mb-5">
            <p className="text-sm text-[#6B7B6C]">Manage catalog — {products.length} items</p>
            <Button onClick={openNew} className="btn-primary rounded-full" data-testid="admin-add-product">
              <Plus className="h-4 w-4 mr-1" /> Add product
            </Button>
          </div>
          <div className="overflow-x-auto rounded-2xl border border-[#E5E5E0] bg-white">
            <table className="w-full text-sm" data-testid="admin-products-table">
              <thead className="bg-[#F0EFEA] text-[#2C3E2D]">
                <tr>
                  <th className="text-left p-3">Product</th>
                  <th className="text-left p-3">Category</th>
                  <th className="text-left p-3">Price</th>
                  <th className="text-left p-3">Stock</th>
                  <th className="text-left p-3">Featured</th>
                  <th className="text-right p-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {products.map((p) => (
                  <tr key={p.product_id} className="border-t border-[#E5E5E0]" data-testid={`admin-product-row-${p.product_id}`}>
                    <td className="p-3">
                      <div className="flex items-center gap-3">
                        {p.image_url && <img src={p.image_url} className="h-9 w-9 rounded-lg object-cover" alt={p.name} />}
                        <span className="font-medium text-[#2C3E2D]">{p.name}</span>
                      </div>
                    </td>
                    <td className="p-3 capitalize">{p.category}</td>
                    <td className="p-3">₹{p.price}</td>
                    <td className="p-3">{p.stock}</td>
                    <td className="p-3">{p.featured ? "Yes" : "No"}</td>
                    <td className="p-3 text-right">
                      <button onClick={() => openEdit(p)} className="p-2 rounded-full hover:bg-[#F0EFEA]" data-testid={`edit-${p.product_id}`}><Edit2 className="h-4 w-4" /></button>
                      <button onClick={() => deleteProduct(p.product_id)} className="p-2 rounded-full hover:bg-red-50 text-red-600" data-testid={`delete-${p.product_id}`}><Trash2 className="h-4 w-4" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "orders" && (
        <div>
          <p className="text-sm text-[#6B7B6C] mb-5">{orders.length} order(s)</p>
          <div className="space-y-3" data-testid="admin-orders-list">
            {orders.map((o) => (
              <div key={o.order_id} className="rounded-2xl border border-[#E5E5E0] bg-white p-5" data-testid={`admin-order-${o.order_id}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="text-xs uppercase tracking-widest text-[#6B7B6C]">{new Date(o.created_at).toLocaleString()}</div>
                    <div className="font-heading text-lg text-[#2C3E2D]">{o.order_id}</div>
                    <div className="text-sm text-[#6B7B6C]">{o.address.full_name} · {o.address.phone} · {o.address.city}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-heading text-lg text-[#2C3E2D]">₹{o.total.toFixed(2)}</div>
                    <div className="text-xs text-[#6B7B6C]">{o.payment_method === "cod" ? "Cash on Delivery" : "Razorpay"}</div>
                    {o.coupon_code && <div className="text-xs text-[#81B29A]">Coupon: {o.coupon_code}</div>}
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  {o.items.slice(0, 4).map((i) => (
                    <span key={i.product_id} className="text-xs rounded-full bg-[#F0EFEA] px-2 py-1">
                      {i.name} × {i.quantity}
                    </span>
                  ))}
                  {o.items.length > 4 && <span className="text-xs text-[#6B7B6C]">+{o.items.length - 4} more</span>}
                </div>
                <div className="mt-4">
                  <Select value={o.order_status} onValueChange={(v) => setOrderStatus(o.order_id, v)}>
                    <SelectTrigger className="w-56 h-10 rounded-full bg-white" data-testid={`order-status-${o.order_id}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ORDER_STATUSES.map((s) => (
                        <SelectItem key={s} value={s} className="capitalize">{s.replace("_", " ")}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "coupons" && (
        <div data-testid="admin-coupons">
          <div className="flex justify-between items-center mb-5">
            <p className="text-sm text-[#6B7B6C]">{coupons.length} coupon(s)</p>
            <Button onClick={() => { setCouponForm(emptyCoupon); setCouponOpen(true); }} className="btn-primary rounded-full" data-testid="admin-add-coupon">
              <Plus className="h-4 w-4 mr-1" /> New coupon
            </Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {coupons.map((c) => (
              <div key={c.code} className="rounded-2xl border border-[#E5E5E0] bg-white p-5" data-testid={`coupon-card-${c.code}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2 tick-label"><Tag className="h-3 w-3" /> {c.type === "percent" ? `${c.value}% OFF` : `₹${c.value} OFF`}</div>
                    <div className="font-heading text-2xl text-[#2C3E2D] mt-1">{c.code}</div>
                    <div className="text-sm text-[#6B7B6C] mt-1">{c.description || "—"}</div>
                    <div className="text-xs text-[#6B7B6C] mt-2">Min subtotal: ₹{c.min_subtotal}{c.max_discount ? ` · Max: ₹${c.max_discount}` : ""}</div>
                    {c.expires_at && <div className="text-xs text-[#6B7B6C]">Expires: {c.expires_at.split("T")[0]}</div>}
                  </div>
                  <button onClick={() => deleteCoupon(c.code)} className="p-2 rounded-full hover:bg-red-50 text-red-600" data-testid={`del-coupon-${c.code}`}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Product Modal */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-lg max-h-[92vh] overflow-y-auto" data-testid="product-form-dialog">
          <DialogHeader><DialogTitle>{editing ? "Edit product" : "Add product"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Field label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="pf-name" />
            <div>
              <Label className="text-xs uppercase tracking-widest text-[#6B7B6C]">Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger className="mt-2 h-11 rounded-xl bg-white" data-testid="pf-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Price (₹)" type="number" value={form.price} onChange={(v) => setForm({ ...form, price: v })} testid="pf-price" />
              <Field label="Unit" value={form.unit} onChange={(v) => setForm({ ...form, unit: v })} testid="pf-unit" />
            </div>

            <div>
              <Label className="text-xs uppercase tracking-widest text-[#6B7B6C]">Image</Label>
              <div className="mt-2 flex items-center gap-3">
                {form.image_url ? <img src={form.image_url} alt="preview" className="h-14 w-14 rounded-lg object-cover border border-[#E5E5E0]" /> : <div className="h-14 w-14 rounded-lg bg-[#F0EFEA] border border-[#E5E5E0]" />}
                <input ref={fileRef} type="file" accept="image/*" onChange={(e) => uploadImage(e.target.files?.[0])} className="hidden" data-testid="pf-file-input" />
                <Button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className="btn-outline-dark rounded-lg h-10 px-4 text-sm" data-testid="pf-upload-btn">
                  <Upload className="h-4 w-4 mr-1" /> {uploading ? "Uploading…" : "Upload image"}
                </Button>
              </div>
              <Input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} placeholder="Or paste an image URL" className="mt-2 h-11 rounded-xl bg-white" data-testid="pf-image" />
            </div>

            <Field label="Stock" type="number" value={form.stock} onChange={(v) => setForm({ ...form, stock: v })} testid="pf-stock" />
            <Field label="Description" value={form.description} onChange={(v) => setForm({ ...form, description: v })} testid="pf-desc" />
            <label className="flex items-center gap-2 text-sm text-[#2C3E2D]">
              <input type="checkbox" checked={!!form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} data-testid="pf-featured" />
              Featured on homepage
            </label>
            <Button onClick={saveProduct} className="btn-primary rounded-full w-full h-11" data-testid="pf-save">
              {editing ? "Save changes" : "Add product"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Coupon Modal */}
      <Dialog open={couponOpen} onOpenChange={setCouponOpen}>
        <DialogContent className="bg-white max-w-md" data-testid="coupon-form-dialog">
          <DialogHeader><DialogTitle>Create coupon</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Field label="Code (e.g. GRAMO20)" value={couponForm.code} onChange={(v) => setCouponForm({ ...couponForm, code: v.toUpperCase() })} testid="cf-code" />
            <div>
              <Label className="text-xs uppercase tracking-widest text-[#6B7B6C]">Type</Label>
              <Select value={couponForm.type} onValueChange={(v) => setCouponForm({ ...couponForm, type: v })}>
                <SelectTrigger className="mt-2 h-11 rounded-xl bg-white" data-testid="cf-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="percent">Percentage (%)</SelectItem>
                  <SelectItem value="flat">Flat amount (₹)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label={couponForm.type === "percent" ? "Percent (%)" : "Amount (₹)"} type="number" value={couponForm.value} onChange={(v) => setCouponForm({ ...couponForm, value: v })} testid="cf-value" />
              <Field label="Min subtotal (₹)" type="number" value={couponForm.min_subtotal} onChange={(v) => setCouponForm({ ...couponForm, min_subtotal: v })} testid="cf-minsub" />
            </div>
            {couponForm.type === "percent" && (
              <Field label="Max discount (₹) — optional" type="number" value={couponForm.max_discount} onChange={(v) => setCouponForm({ ...couponForm, max_discount: v })} testid="cf-maxdisc" />
            )}
            <Field label="Description" value={couponForm.description} onChange={(v) => setCouponForm({ ...couponForm, description: v })} testid="cf-desc" />
            <Field label="Expires at (YYYY-MM-DD, optional)" value={couponForm.expires_at} onChange={(v) => setCouponForm({ ...couponForm, expires_at: v })} testid="cf-expires" />
            <Button onClick={saveCoupon} className="btn-primary rounded-full w-full h-11" data-testid="cf-save">Create</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const TabBtn = ({ value, tab, setTab, children }) => (
  <button onClick={() => setTab(value)} className={`chip ${tab === value ? "active" : ""}`} data-testid={`admin-tab-${value}`}>{children}</button>
);

const Stat = ({ Icon, label, value, testid }) => (
  <div className="rounded-2xl border border-[#E5E5E0] bg-white p-5" data-testid={testid}>
    <div className="h-9 w-9 rounded-full bg-[#F0EFEA] inline-flex items-center justify-center mb-3">
      <Icon className="h-4 w-4 text-[#81B29A]" />
    </div>
    <div className="text-xs uppercase tracking-widest text-[#6B7B6C]">{label}</div>
    <div className="font-heading text-3xl text-[#2C3E2D] mt-1">{value}</div>
  </div>
);

const Field = ({ label, value, onChange, testid, type = "text" }) => (
  <div>
    <Label className="text-xs uppercase tracking-widest text-[#6B7B6C]">{label}</Label>
    <Input type={type} value={value} onChange={(e) => onChange(e.target.value)} className="mt-2 h-11 rounded-xl bg-white" data-testid={testid} />
  </div>
);

export default Admin;
